import React from "react"
import * as Yup from "yup"
import {
  Box, Divider, Flex, FormLabel, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import pluralize from "pluralize"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import { putEvent } from "api/events"
import { refetchFormStatuses, setCurrentEvent } from "context/actions"
import { useCurrentUser } from "queries/users"
import usePermissions from "services/permissions"
import { useModalStore } from "services/useModal"
import { DioboxEvent, ModalName } from "sharedTypes"
import { successToast } from "utilities/toasts"
import { EVENT_CAPACITY_ARTICLE_LINK } from "utilities/externalLinks"
import { eventSettingsPath } from "utilities/routes"
import { CapacityRing } from "pages/EventShow/Forms/CapacityStat"
import useResolvedTranslations from "pages/EventShow/Settings/LanguageTemplates/useResolvedTranslations"

interface Props {
  event: DioboxEvent
  languageTemplateId?: string | null
  onHide: () => void
}

type Values = {
  capacity: number | string | null
}

const validationSchema = Yup.object().shape({
  capacity: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .typeError("Enter a number of guests")
    .integer("Enter a whole number of guests")
    .min(1, "Capacity must be between 1 and 50,000")
    .max(50000, "Capacity must be between 1 and 50,000")
    .nullable(),
})

// Single characters other than digits; shortcuts, arrows, and Backspace still work
const allowDigitsOnly = (event: React.KeyboardEvent) => {
  if (event.key.length === 1 && !/\d/.test(event.key) && !event.metaKey && !event.ctrlKey) {
    event.preventDefault()
  }
}

const EventCapacity = ({ event, languageTemplateId, onHide }: Props) => {
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const { confirmedGuests } = event.counts

  const onSubmit = async (values: Values) => {
    const capacity = Number(values.capacity) || null
    const { data } = await putEvent(event.id, { ...event, capacity })
    setCurrentEvent(queryClient, data, currentUser)
    await refetchFormStatuses(queryClient, event.id)
    successToast({ title: "Event capacity saved" })
    onHide()
  }

  return (
    <ModalForm
      title="Event Capacity"
      size="lg"
      onClose={onHide}
      initialValues={{ capacity: event.capacity ?? "" }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      disableSubmitWhenNotDirty
    >
      {({ control, watch }) => {
        const capacity = Number(watch("capacity")) || null

        return (
          <>
            <Text mb={6}>
              Limit the number of Confirmed guests, including plus-ones and guests added manually.
              Once it&apos;s reached, all registration forms close, except for Waitlists.
              They reopen if spots become available.{" "}
              <Link href={EVENT_CAPACITY_ARTICLE_LINK} isExternal>Learn more</Link>
            </Text>
            <FormLabel>Capacity</FormLabel>
            <Flex
              align="center"
              justify="space-between"
              gap={4}
              bg="gray.50"
              borderWidth={1}
              borderRadius="md"
              px={6}
              py={4}
            >
              <Flex align="center" gap={3}>
                <Box w={36}>
                  <TextField
                    name="capacity"
                    control={control}
                    variant="outline"
                    placeholder="Unlimited"
                    size="xl"
                    fontWeight={capacity ? "semibold" : undefined}
                    showDeleteButton
                    autoComplete="off"
                    onKeyDown={allowDigitsOnly}
                    mb={0}
                  />
                </Box>
                <Text color="gray.600">guests</Text>
              </Flex>
              <Flex align="center" gap={3}>
                <Box textAlign="right" lineHeight="short">
                  <Text>
                    <Text as="span" fontSize="xl" fontWeight="semibold">{confirmedGuests.toLocaleString()}</Text>
                    {" "}confirmed
                  </Text>
                  <Text
                    fontSize="sm"
                    color={capacity && confirmedGuests >= capacity ? "orange.600" : "gray.600"}
                  >
                    {spotsLeftLabel(capacity, confirmedGuests)}
                  </Text>
                </Box>
                <CapacityRing capacity={capacity} confirmedGuests={confirmedGuests} size="40px" />
              </Flex>
            </Flex>
            <CapacityWarning
              capacity={capacity}
              confirmedGuests={confirmedGuests}
              saved={capacity === event.capacity}
            />
            <Divider my={6} />
            <GuestMessage
              event={event}
              languageTemplateId={languageTemplateId}
              onNavigate={onHide}
            />
          </>
        )
      }}
    </ModalForm>
  )
}

const spotsLeftLabel = (capacity: number | null, confirmedGuests: number) => {
  if (!capacity) return "No limit"
  const spotsLeft = capacity - confirmedGuests
  if (spotsLeft <= 0) return "Full"

  return `${spotsLeft.toLocaleString()} ${pluralize("spot", spotsLeft)} left`
}

type WarningProps = { capacity: number | null, confirmedGuests: number, saved: boolean }

const CapacityWarning = ({ capacity, confirmedGuests, saved }: WarningProps) => {
  if (!capacity || confirmedGuests < capacity) return null

  const over = confirmedGuests - capacity
  const warning = () => {
    if (saved && over > 0) {
      return `${over.toLocaleString()} ${pluralize("guest", over)} over capacity. Forms stay closed until spots open up.`
    }
    if (saved) return "Forms stay closed until spots open up."
    if (over > 0) {
      return `${confirmedGuests.toLocaleString()} guests are already confirmed. Your forms will close, but no one will be removed.`
    }

    return "Your forms will close as soon as you save."
  }

  return (
    <Box mt={3} color="orange.600">
      <Flex align="flex-start" gap={2}>
        <Flex h="1.5em" align="center" flexShrink={0}>
          <Icon icon="alert" size={4} />
        </Flex>
        <Text>{warning()}</Text>
      </Flex>
      {saved && over > 0 && (
        <Text fontSize="sm" color="gray.600" mt={1} ml={6}>
          This can happen when your team adds or confirms guests, the capacity is lowered,
          ticket orders are paid as the last spots fill, or guests submit forms at the same moment.
        </Text>
      )}
    </Box>
  )
}

type GuestMessageProps = {
  event: DioboxEvent
  languageTemplateId?: string | null
  onNavigate: () => void
}

const GuestMessage = ({ event, languageTemplateId, onNavigate }: GuestMessageProps) => {
  const history = useHistory()
  const { canViewSettings } = usePermissions()
  const { capacityReachedMessage } = useResolvedTranslations(languageTemplateId)
  // Opened over the form window, which would stay open on top of the settings page
  const overAnotherWindow = useModalStore((s) => s.modal?.name !== ModalName.EventCapacity)

  const openLanguageTemplates = (e: React.MouseEvent) => {
    e.preventDefault()
    onNavigate()
    history.push(eventSettingsPath(event.id, "language-templates"))
  }

  return (
    <>
      <FormLabel>Message Guests Will See</FormLabel>
      <Box bg="gray.50" borderWidth={1} borderRadius="md" px={6} py={4}>
        <Text>{capacityReachedMessage}</Text>
      </Box>
      <Text mt={2}>
        You can change this message in{" "}
        {canViewSettings && !overAnotherWindow
          ? <Link href={eventSettingsPath(event.id, "language-templates")} onClick={openLanguageTemplates}>Language Templates</Link>
          : "Language Templates"}.
      </Text>
    </>
  )
}

export default EventCapacity
